import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const OWNED_TYPES = ['deliverable', 'work_package', 'milestone', 'risk', 'issue', 'dependency'] as const;
export type OwnedType = (typeof OWNED_TYPES)[number];
export type OwnedCounts = Record<OwnedType, { open: number; done: number }>;

/** One person's part in one project. */
export interface TeamProjectRole {
  projectId: string;
  projectName: string;
  portfolio: string | null;
  roles: string[];
}

export interface TeamPerson {
  key: string;
  name: string;
  /** Project manager if they are one on any project, otherwise their most common role. */
  primaryRole: string;
  /** Portfolio they work in most. */
  portfolio: string | null;
  projects: TeamProjectRole[];
  owned: OwnedCounts;
  open: number;
  done: number;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const emptyOwned = (): OwnedCounts => Object.fromEntries(OWNED_TYPES.map((t) => [t, { open: 0, done: 0 }])) as OwnedCounts;
const PAGE = 5000;

interface Payload { name?: unknown; roles?: unknown; owned?: Record<string, { open?: unknown; done?: unknown }> }

/**
 * The delivery team behind the projects, read from the owners named in project registers.
 * The same person on several projects is one entry with their part on each project and what
 * they own (open and done). Nothing needs approving: people are read straight from the facts.
 */
export function useTeamDirectory() {
  const [people, setPeople] = useState<TeamPerson[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [candRes, itemsRes] = await Promise.all([
        supabase.from('document_candidates').select('dossier_key, matched_project_id').not('dossier_key', 'is', null).not('matched_project_id', 'is', null).range(0, PAGE - 1),
        supabase.from('project_intel_items').select('dossier_key, payload').eq('kind', 'team_member').neq('status', 'rejected').range(0, PAGE - 1),
      ]);
      const dossierProject = new Map<string, string>();
      for (const c of candRes.data ?? []) if (c.dossier_key && c.matched_project_id) dossierProject.set(c.dossier_key, c.matched_project_id);
      const items = (itemsRes.data ?? []) as Array<{ dossier_key: string; payload: Payload }>;

      const ids = [...new Set(items.map((i) => dossierProject.get(i.dossier_key)).filter((x): x is string => !!x))];
      const projects = new Map<string, { name: string; portfolio: string | null }>();
      if (ids.length > 0) {
        const { data } = await supabase.from('projects_sync').select('id, name, portfolio_id').in('id', ids.slice(0, 1000));
        for (const p of data ?? []) projects.set(p.id, { name: p.name, portfolio: p.portfolio_id ?? null });
      }

      const byPerson = new Map<string, TeamPerson>();
      for (const it of items) {
        const projectId = dossierProject.get(it.dossier_key);
        const name = typeof it.payload.name === 'string' ? it.payload.name.trim() : '';
        if (!projectId || !name) continue;
        const roles = Array.isArray(it.payload.roles) ? it.payload.roles.filter((r): r is string => typeof r === 'string') : [];
        const key = norm(name);
        let person = byPerson.get(key);
        if (!person) {
          person = { key, name, primaryRole: '', portfolio: null, projects: [], owned: emptyOwned(), open: 0, done: 0 };
          byPerson.set(key, person);
        }
        const proj = projects.get(projectId);
        person.projects.push({ projectId, projectName: proj?.name ?? 'Unnamed project', portfolio: proj?.portfolio ?? null, roles });
        for (const t of OWNED_TYPES) {
          const o = it.payload.owned?.[t];
          const open = typeof o?.open === 'number' ? o.open : 0;
          const done = typeof o?.done === 'number' ? o.done : 0;
          person.owned[t].open += open;
          person.owned[t].done += done;
          person.open += open;
          person.done += done;
        }
      }
      for (const p of byPerson.values()) {
        const counts = new Map<string, number>();
        for (const pr of p.projects) for (const r of pr.roles) counts.set(r, (counts.get(r) ?? 0) + 1);
        p.primaryRole = counts.has('Project manager') ? 'Project manager' : ([...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'Team member');
        const byPortfolio = new Map<string, number>();
        for (const pr of p.projects) if (pr.portfolio) byPortfolio.set(pr.portfolio, (byPortfolio.get(pr.portfolio) ?? 0) + 1);
        p.portfolio = [...byPortfolio].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
        p.projects.sort((a, b) => (a.portfolio ?? '').localeCompare(b.portfolio ?? '') || a.projectName.localeCompare(b.projectName));
      }
      if (!cancelled) {
        setPeople([...byPerson.values()].sort((a, b) => b.open - a.open || b.projects.length - a.projects.length || a.name.localeCompare(b.name)));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = useMemo(() => {
    const open = people.reduce((n, p) => n + p.open, 0);
    const done = people.reduce((n, p) => n + p.done, 0);
    const byType = Object.fromEntries(OWNED_TYPES.map((t) => [t, people.reduce((n, p) => n + p.owned[t].open + p.owned[t].done, 0)])) as Record<OwnedType, number>;
    return { open, done, byType, busiest: people.reduce((m, p) => Math.max(m, p.open), 0) };
  }, [people]);

  return { people, totals, loading };
}
